import { Component, HostListener, Input, OnInit } from '@angular/core';
import { finalize, take, tap } from 'rxjs';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import { UserService } from 'src/app/authentication/services/user.service';
import { UserProfile } from '../../../authentication/profile/models/user-profile';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IDeactivateComponent } from '../../../authentication/i-deactivate/i-deactivate.component';
import { TranslateService } from '@ngx-translate/core';
import { ChangeLangEventService } from '../../../navigation/menu/components/language-nav-menu/service/change-lang-event.service';
import { LanguageCode } from 'src/app/navigation/menu/components/language-nav-menu/data/laguage-code';
import { ToastMessageService } from '../../../shared/services/toast-message.service';
import { nonNil, trimFormValues } from '../../../shared/utility';
import { LoginRedirectionService } from 'src/app/authentication/services/login-redirection.service';

interface ValidationRule {
    minLength: number;
    maxLength: number;
    pattern: string;
}

@Component({
    selector: 'app-profile-page',
    templateUrl: './profile-page.component.html',
    styleUrls: ['./profile-page.component.scss'],
    standalone: false,
})
export class ProfilePageComponent implements OnInit, IDeactivateComponent {
    //HTML imports
    protected readonly LanguageCode = LanguageCode;
    //
    @Input() isUserExperimental: boolean;

    //Extra props outside form to extra security against changes
    // asign empty value to prevent flickering as form would be first filled with 'undefined'
    username: string = '';
    userEmail: string = '';
    updating: boolean = false;

    readonly TOAST_MESSAGE_DELAY = { delay: 2000 };

    // Base character classes for validation patterns
    private static readonly LETTERS = 'A-Za-z';
    private static readonly GERMAN_CHARS = 'ßÄäÖöÜü';
    private static readonly FRENCH_CHARS = "àâäèéêëîïôœùûüÿçÀÂÄÈÉÊËÎÏÔŒÙÛÜŸÇ'";
    private static readonly EXTENDED_CHARS = `${ProfilePageComponent.LETTERS}${ProfilePageComponent.GERMAN_CHARS}${ProfilePageComponent.FRENCH_CHARS}`;

    // Helper to build regex patterns with extended character support (German/French)
    private static buildExtendedPattern(extras: string = ''): string {
        return `^[${ProfilePageComponent.EXTENDED_CHARS}${extras}]*$`;
    }

    // Helper to build regex patterns with basic letters only
    private static buildBasicPattern(extras: string = ''): string {
        return `^[${ProfilePageComponent.LETTERS}${extras}]*$`;
    }

    protected readonly validationRules: Record<string, ValidationRule> = {
        country: {
            minLength: 0,
            maxLength: 30,
            pattern: ProfilePageComponent.buildExtendedPattern('\\s-'),
        },
        city: {
            minLength: 0,
            maxLength: 30,
            pattern: ProfilePageComponent.buildExtendedPattern('\\s-'),
        },
        postalCode: {
            minLength: 0,
            maxLength: 10,
            pattern: ProfilePageComponent.buildBasicPattern('0-9\\s'),
        },
        addressSuffix: {
            minLength: 0,
            maxLength: 30,
            pattern: ProfilePageComponent.buildExtendedPattern('0-9\\s_.,-'),
        },
        streetAddress: {
            minLength: 0,
            maxLength: 30,
            pattern: ProfilePageComponent.buildExtendedPattern('0-9\\s.,-'),
        },
        firstName: {
            minLength: 0,
            maxLength: 20,
            pattern: ProfilePageComponent.buildExtendedPattern('\\s.-'),
        },
        lastName: {
            minLength: 0,
            maxLength: 20,
            pattern: ProfilePageComponent.buildExtendedPattern('\\s.-'),
        },
    };

    // Field configurations for template iteration
    protected readonly nameFields = [
        {
            id: 'firstName',
            translationKey: 'user.userArea.profile.name.firstName.errors',
            cssClass: 'left-border-radius opposite-border',
        },
        {
            id: 'lastName',
            translationKey: 'user.userArea.profile.name.lastName.errors',
            cssClass: 'right-border-radius',
        },
    ];

    protected readonly addressFields = [
        {
            id: 'streetAddress',
            labelKey: 'user.userArea.profile.streetAddress.label',
            errorKey: 'user.userArea.profile.streetAddress.errors',
        },
        {
            id: 'addressSuffix',
            labelKey: 'user.userArea.profile.addressSuffix.label',
            errorKey: 'user.userArea.profile.addressSuffix.errors',
        },
        {
            id: 'postalCode',
            labelKey: 'user.userArea.profile.postalCode.label',
            errorKey: 'user.userArea.profile.postalCode.errors',
        },
        {
            id: 'city',
            labelKey: 'user.userArea.profile.city.label',
            errorKey: 'user.userArea.profile.city.errors',
        },
        {
            id: 'country',
            labelKey: 'user.userArea.profile.country.label',
            errorKey: 'user.userArea.profile.country.errors',
        },
    ];

    user: UserProfile;
    profileForm: FormGroup;

    constructor(
        private userService: UserService,
        private authenticationService: AuthenticationService,
        private translateService: TranslateService,
        private changeLangEventService: ChangeLangEventService,
        private messageService: ToastMessageService,
        private redirectionService: LoginRedirectionService
    ) {}

    ngOnInit(): void {
        // Build form controls from validation rules
        const formControls: Record<string, FormControl> = {};
        for (const [field, rule] of Object.entries(this.validationRules)) {
            formControls[field] = new FormControl('', [
                Validators.minLength(rule.minLength),
                Validators.maxLength(rule.maxLength),
                Validators.pattern(rule.pattern),
            ]);
        }
        formControls['language'] = new FormControl();
        formControls['enableNonEuRegions'] = new FormControl(false);

        this.profileForm = new FormGroup(formControls);

        if (this.authenticationService.isLoggedIn()) {
            this.userService
                .getUser(this.authenticationService.currentUserValue.id)
                .pipe(
                    take(1),
                    tap((user: UserProfile) => {
                        this.user = user;
                        this.username = user.displayName;
                        this.userEmail = user.email;

                        this.patchForm(user);
                    })
                )
                .subscribe();
        } else {
            console.error('There is an internal error.');
            this.authenticationService.signOut();
        }
    }

    private patchForm(user: UserProfile) {
        this.profileForm.patchValue({
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            streetAddress: user?.streetAddress || '',
            addressSuffix: user?.addressSuffix || '',
            postalCode: user?.postalCode || '',
            city: user?.city || '',
            country: user?.country || '',
            language: user?.language,
            enableNonEuRegions: user?.enableNonEuRegions ?? false,
        });

        if (user?.language) {
            this.applyLanguageChange(user.language as LanguageCode);
        }

        // Mark form as pristine after loading initial values
        this.profileForm.markAsPristine();
    }

    /**
     * Checks if there are unsaved changes in the form
     */
    hasUnsavedChanges(): boolean {
        return this.profileForm?.dirty ?? false;
    }

    /**
     * Implementation of IDeactivateComponent interface
     * Shows confirmation dialog when navigating away with unsaved changes
     */
    canExit(): boolean {
        if (this.hasUnsavedChanges()) {
            return window.confirm(
                this.translateService.instant(
                    'user.userArea.profile.unsavedChangesWarning'
                )
            );
        }
        return true;
    }

    /**
     * Handles browser beforeunload event to show warning when closing tab with unsaved changes
     */
    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event: BeforeUnloadEvent): void {
        if (this.hasUnsavedChanges()) {
            event.preventDefault();
            event.returnValue = false;
        }
    }

    /**
     * Saves changes when the Save Changes button is clicked
     * Currently only handles language changes (stored in localStorage)
     * Other profile fields are read-only from Keycloak/user administration
     */
    saveChanges(): void {
        if (!this.hasUnsavedChanges()) {
            return;
        }

        // Trim whitespace from all string fields
        const formValues = trimFormValues(this.profileForm.value);

        // Apply language change if modified
        // Language is stored in localStorage, not in the backend
        if (formValues.language) {
            this.applyLanguageChange(formValues.language);
        }

        if (this.profileForm.valid) {
            this.updating = true;
            this.userService
                .updateUser(formValues, this.user.id)
                .pipe(
                    finalize(() => {
                        this.updating = false;
                    })
                )
                .subscribe({
                    next: (_) => {
                        this.messageService.showTranslatedSuccessMsg(
                            'user.userArea.profile.saveChangesSucess',
                            this.TOAST_MESSAGE_DELAY
                        );
                        // Mark form as clean after changes are applied
                        this.profileForm.markAsPristine();
                    },
                    error: (err) => {
                        if (err.status < 500) {
                            // backend denied update due to validation rules
                            this.messageService.showTranslatedErrorMsg(
                                'user.userArea.profile.saveChangesFailure',
                                this.TOAST_MESSAGE_DELAY
                            );
                        } else {
                            this.messageService.showTranslatedErrorMsg(
                                'user.userArea.profile.saveChangesError',
                                this.TOAST_MESSAGE_DELAY
                            );
                        }
                    },
                });
        }
    }

    /**
     * Applies the language change to the application by notifying the language service
     * The language is persisted in localStorage via app.component subscription
     */
    private applyLanguageChange(languageCode: LanguageCode): void {
        if (nonNil(languageCode)) {
            this.changeLangEventService.setLangSubject(languageCode);
        }
    }

    /**
     * Redirects to keycloak change password mask
     */
    onChangePassword(): void {
        this.redirectionService.changePassword();
    }

    /**
     * Placeholder for account deletion functionality.
     * TODO: Implement account deletion dialog in future ticket.
     */
    onDeleteAccount(): void {
        console.log(
            'Delete account clicked - functionality not yet implemented'
        );
    }

    focusFieldOnError(event: FocusEvent, fieldId: string): void {
        const profileFromField = this.profileForm?.get(fieldId);
        if (nonNil(profileFromField)) {
            profileFromField.setValue(profileFromField.value.trim());

            if (profileFromField.invalid) {
                const target = event.target as HTMLInputElement;
                target.focus();
            }
        }
    }
}
